import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import styles from "./LayersCard.module.css";
import {
  readableLayer,
  readableLimitOfLiabilityFromLayer,
  sortedLayersFromReport,
} from "./util";

import { SafeReportResponse } from "../Conversion";
import { formatTwoDecimals } from "../utils";
import * as React from "react";
import { useState, useMemo, useContext } from "react";
import { ThemeContext } from "../../../themes/Theme";
import {
  HIGHLIGHT_WARNING_LIGHT_MODE,
  HIGHLIGHT_WARNING_DARK_MODE,
  HIGHLIGHT_ERROR_LIGHT_MODE,
  HIGHLIGHT_ERROR_DARK_MODE,
  HIGHLIGHT_USER_OVERRIDE_LIGHT_MODE,
  HIGHLIGHT_USER_OVERRIDE_DARK_MODE,
} from "../dashboard/util";

import { cloneDeep } from "lodash";
import * as client from "../../../client";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";
import {
  DBPerilType,
  getEffectiveIncludedPerils,
  perilSentenceFromEnabledPerils,
  readableNameFromPeril,
} from "../general-input/util";
import { useCalculatedPremium, useUpdateReport } from "../data";
import { v4 as uuid } from "uuid";
import { formatExactMoneyDollars } from "../Money";
import {
  WarningLevel,
  warningSeverity,
  WARNING_DISPLAY_NAMES,
} from "../rater/warnings";

const maxWarningLevel = (keys: string[]): WarningLevel | undefined => {
  if (keys.some((k) => warningSeverity(k) === "critical")) return "critical";
  return keys.length > 0 ? "info" : undefined;
};

type LayersCardProps = {
  specialtyPropertyInfo: SafeReportResponse;
  title?: string;
};

type SourcePremiumData = {
  aal: number | null;
  multiple: number | null;
  multipleTooltip: string | null;
  layerPremium: number | null;
  sharePremium: number | null;
  warningsTooltip: string | null;
  missingData: boolean;
  warningLevel: WarningLevel | undefined;
  overrideType: "multiple" | "premium" | null;
  /** Pre-override multiple (null for mixed/direct-premium peril types or rows without parts). */
  originalMultiple: number | null;
  /** Pre-override layer premium (null for rows without parts). */
  originalLayerPremium: number | null;
  /** Pre-override share premium (null for rows without parts). */
  originalSharePremium: number | null;
  /** True when an override update is pending and derived values are not yet recomputed by the backend. */
  stale: boolean;
  /**
   * Peril code that edits on this source-cell write to. Set when the cell
   * represents exactly one code (single-code peril-type rows, or peril-code
   * sub-rows). `null` when this source-cell has 0 codes (no data) or aggregates
   * multiple codes (multi-code peril-type rows) — in which case the cell is
   * read-only and edits must happen on the corresponding sub-row.
   */
  editablePerilCode: string | null;
  /**
   * Whether the underlying code accepts a `multiple` override (i.e. is AAL-
   * based, not direct-premium).
   */
  acceptsMultipleOverride: boolean;
};

type PerilDataSource = "air" | "rms" | "spatialkat";
type DisplayPerilDataSource =
  | "air"
  | "rms"
  | "spatialkat"
  | "lum"
  | "tbd"
  | "mix";

type PremiumPerilRowBase = {
  layer: client.Layer;
  layerId: string;
  air: SourcePremiumData;
  rms: SourcePremiumData;
  spatialkat: SourcePremiumData;
  /** Resolved premium data for the "Results" column. */
  resolved: SourcePremiumData;
  target: number | null;
  isFirstInGroup: boolean;
};

type PerilTypePremiumPerilRow = PremiumPerilRowBase & {
  rowKind: "perilType";
  peril: DBPerilType;
  resolvedSource: PerilDataSource;
  partSources: DisplayPerilDataSource[];
};
type PerilCodePremiumPerilRow = PremiumPerilRowBase & {
  rowKind: "perilCode";
  peril: DBPerilType;
  perilCode: string;
  resolvedSource: PerilDataSource;
};
type CommissionAdjPerilRow = PremiumPerilRowBase & {
  rowKind: "commissionAdj";
  peril: "Commission Adj.";
  factor: number;
  commissionPercent: number | null;
};
type PremiumPerilRow =
  | (PremiumPerilRowBase & { rowKind: "total"; peril: "Total" })
  | CommissionAdjPerilRow
  | PerilCodePremiumPerilRow
  | PerilTypePremiumPerilRow;

const displayPerilDataSource = (
  perilCode: string,
  resolvedSource: PerilDataSource
): DisplayPerilDataSource => (perilCode === "AOP" ? "lum" : resolvedSource);

const displayPerilDataSource2 = (
  rowData: PerilTypePremiumPerilRow
): DisplayPerilDataSource =>
  rowData.peril === "equipment_breakdown"
    ? "tbd"
    : rowData.partSources.length > 1
      ? "mix"
      : rowData.partSources[0]!;

const PERIL_CODE_DISPLAY_NAMES = {
  PWH: "Hurricane - Wind Only",
  PES: "Earthquake - Ground Shake",
  PFL: "Inland Flood",
  PWB: "Wildfire",
  PWW: "Winter Storm",
  PWT: "Severe Thunderstorm",
  AOP: "All Other Perils",
  EBP: "Equipment Breakdown",
} as const;

const perilCodeDisplayName = (code: string): string => {
  return (PERIL_CODE_DISPLAY_NAMES as Record<string, string>)[code] ?? code;
};

const SOURCE_LABELS: Record<DisplayPerilDataSource, string> = {
  air: "AIR",
  rms: "RMS",
  spatialkat: "KAT",
  lum: "LUM",
  tbd: "TBD",
  mix: "MIX",
};

const SOURCE_BADGE_CLASSES: Record<DisplayPerilDataSource, string> = {
  air: "sourceLabelAir",
  rms: "sourceLabelRms",
  spatialkat: "sourceLabelKat",
  lum: "sourceLabelLum",
  mix: "sourceLabelLum",
  tbd: "",
};

const PERIL_TYPE_ORDER: DBPerilType[] = [
  "aop",
  "named_storm",
  "earthquake",
  "earth_movement",
  "flood",
  "wildfire",
  "scs",
  "wind_and_hail",
  "equipment_breakdown",
];

function sortedPerilTypes(
  perilTypes: client.LayerPremiumBreakdown["peril_types"]
) {
  return Object.entries(perilTypes)
    .sort(([a], [b]) => {
      const ai = PERIL_TYPE_ORDER.indexOf(a as DBPerilType);
      const bi = PERIL_TYPE_ORDER.indexOf(b as DBPerilType);
      return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
    })
    .map(([, perilType]) => perilType);
}

/** Pick the resolved source data for a row using the backend's resolved_sources. */
function resolveRowData(
  row: {
    air: SourcePremiumData;
    rms: SourcePremiumData;
    spatialkat: SourcePremiumData;
  },
  resolvedSource: PerilDataSource | null
): SourcePremiumData {
  if (resolvedSource) {
    return row[resolvedSource];
  }
  return EMPTY_SOURCE_DATA;
}

function getRowSource(rowData: PremiumPerilRow): PerilDataSource {
  // The "total" and "commissionAdj" rows have no single resolved source —
  // return any value; the editable-row guards in the edit handlers prevent
  // it from ever being used.
  if (rowData.rowKind === "total" || rowData.rowKind === "commissionAdj") {
    return "rms";
  }
  return rowData.resolvedSource;
}

const EMPTY_SOURCE_DATA: SourcePremiumData = {
  aal: null,
  multiple: null,
  multipleTooltip: null,
  layerPremium: null,
  sharePremium: null,
  warningsTooltip: null,
  missingData: false,
  warningLevel: undefined,
  overrideType: null,
  originalMultiple: null,
  originalLayerPremium: null,
  originalSharePremium: null,
  stale: false,
  editablePerilCode: null,
  acceptsMultipleOverride: false,
};

const norm = (v: number | null | undefined): number | undefined =>
  v != null && v !== 0 ? v : undefined;

/** Build SourcePremiumData for a single peril-code part. */
function buildCodeSourceData(
  part: client.PerilCodePremium,
  resolvedOv: client.SourceOverride | undefined
): SourcePremiumData {
  const backendMultiple =
    part.override_type === "multiple" ? part.override_multiple : undefined;
  const backendPremium =
    part.override_type === "premium" ? part.override_premium : undefined;
  const stale =
    norm(resolvedOv?.multiple) !== norm(backendMultiple) ||
    norm(resolvedOv?.premium) !== norm(backendPremium);

  // Effective values from backend (post-override).
  let overrideType: "multiple" | "premium" | null = part.override_type ?? null;
  let effectiveMultiple: number | null;
  if (part.override_type === "multiple" && part.override_multiple != null) {
    effectiveMultiple = part.override_multiple;
  } else if (
    part.override_type === "premium" &&
    part.override_premium != null
  ) {
    // Direct-premium codes have no meaningful multiple even when overridden.
    effectiveMultiple =
      part.multiplier != null && part.aal > 0
        ? part.override_premium / part.aal
        : null;
  } else {
    effectiveMultiple = part.multiplier;
  }
  let effectiveLayerPremium: number = part.layer_effective;

  // Optimistic overlay while a mutation is in flight.
  if (stale) {
    if (norm(resolvedOv?.multiple) != null) {
      overrideType = "multiple";
      effectiveMultiple = resolvedOv!.multiple!;
    } else if (norm(resolvedOv?.premium) != null) {
      overrideType = "premium";
      effectiveLayerPremium = resolvedOv!.premium!;
    } else {
      overrideType = null;
      effectiveMultiple = part.multiplier;
    }
  }

  return {
    aal: part.aal,
    multiple: effectiveMultiple,
    multipleTooltip: part.multiplier != null ? part.multiplier_reason : null,
    layerPremium: effectiveLayerPremium,
    sharePremium: part.share_effective,
    warningsTooltip: null,
    missingData: part.missing,
    warningLevel: maxWarningLevel(Object.keys(part.warnings)),
    overrideType,
    originalMultiple: overrideType ? part.multiplier : null,
    originalLayerPremium: overrideType ? part.layer_premium : null,
    originalSharePremium: overrideType ? part.share_premium : null,
    stale,
    editablePerilCode: part.peril_code,
    acceptsMultipleOverride: part.multiplier !== null,
  };
}

export const LayersCard: React.FC<LayersCardProps> = ({
  specialtyPropertyInfo,
  title,
}) => {
  const isLightMode = useContext(ThemeContext).theme === "light";
  const sortedLayers = sortedLayersFromReport(specialtyPropertyInfo);
  const updateReport = useUpdateReport(specialtyPropertyInfo.report_id);

  const [sourcesExpanded, setSourcesExpanded] = useState(false);

  // Layer dialog state — shared between add and edit modes
  const [showLayerDialog, setShowLayerDialog] = useState(false);
  const [editingLayerId, setEditingLayerId] = useState<string | null>(null);
  const [newStartingPoint, setNewStartingPoint] = useState<number | null>(null);
  const [newRange, setNewRange] = useState<number | null>(null);
  const [newParticipation, setNewParticipation] = useState<number | null>(null);
  const [newCompetitorPricing, setNewCompetitorPricing] = useState<
    client.CompetitorPricing[]
  >([]);

  const isEditing = editingLayerId !== null;

  const resetLayerDialog = () => {
    setShowLayerDialog(false);
    setEditingLayerId(null);
    setNewStartingPoint(null);
    setNewRange(null);
    setNewParticipation(null);
    setNewCompetitorPricing([]);
  };

  const openAddDialog = () => {
    setEditingLayerId(null);
    setNewStartingPoint(null);
    setNewRange(null);
    setNewParticipation(null);
    setNewCompetitorPricing([]);
    setShowLayerDialog(true);
  };

  const openEditDialog = (layer: client.Layer) => {
    setEditingLayerId(layer.layer_id ?? null);
    setNewStartingPoint(layer.starting_point);
    setNewRange(layer.range);
    setNewParticipation(layer.participation ?? null);
    setNewCompetitorPricing(
      (layer.competitor_pricing ?? []).map((c) => ({ ...c }))
    );
    setShowLayerDialog(true);
  };

  const competitorPricingValid = newCompetitorPricing.every(
    (c) => c.carrier_name.trim().length > 0
  );

  const handleSaveLayer = async () => {
    if (newStartingPoint == null || newRange == null || newRange <= 0) return;
    if (!competitorPricingValid) return;
    const cleanedCompetitorPricing = newCompetitorPricing.map((c) => ({
      carrier_name: c.carrier_name.trim(),
      price: c.price ?? null,
      notes: c.notes?.trim() ? c.notes.trim() : null,
    }));
    const newLayers = cloneDeep(specialtyPropertyInfo.report_json.layer_info);

    if (isEditing) {
      const existing = newLayers.find((L) => L.layer_id === editingLayerId);
      if (!existing) return;
      existing.participation = newParticipation;
      existing.competitor_pricing = cleanedCompetitorPricing;
    } else {
      newLayers.push({
        layer_id: uuid(),
        starting_point: newStartingPoint,
        range: newRange,
        target: null,
        rated: null,
        participation: newParticipation,
        layer_source: "manual",
        is_enabled: true,
        source_overrides: null,
        competitor_pricing: cleanedCompetitorPricing,
      });
    }

    await updateReport.mutateAsync({ layerInfo: newLayers });
    resetLayerDialog();
  };

  // Live preview of the layer description as user fills out the form
  const layerPreview = useMemo(() => {
    if (newRange == null || newRange <= 0) return null;
    return readableLimitOfLiabilityFromLayer({
      starting_point: newStartingPoint ?? 0,
      range: newRange,
      target: null,
      rated: null,
      participation: newParticipation,
    });
  }, [newStartingPoint, newRange, newParticipation]);

  // Fetch calculated premiums from backend
  const {
    data: premiumData,
    isLoading: firstPremiumLoading,
    isFetching: premiumLoading,
    isError: premiumError,
  } = useCalculatedPremium(specialtyPropertyInfo.report_id, true);

  // Build a lookup from backend premium data: layer_id -> LayerPremiumBreakdown
  const premiumByLayer = useMemo(() => {
    if (!premiumData?.layers?.length)
      return new Map<string, client.LayerPremiumBreakdown>();
    return new Map<string, client.LayerPremiumBreakdown>(
      premiumData.layers.map((l) => [l.layer_id, l])
    );
  }, [premiumData]);

  // Premium table state — keyed by layer_id
  const layerOptions = sortedLayers.map((l) => ({
    label: `${readableLayer(l)} (${l.layer_source})`,
    value: l.layer_id!,
  }));
  const enabledLayers = specialtyPropertyInfo.report_json.layer_info
    .filter((L) => L.is_enabled)
    .map((L) => L.layer_id);

  // Resolve layer info, preferring optimistic pending mutation data
  const resolvedLayerInfo = useMemo(() => {
    const layers = specialtyPropertyInfo.report_json.layer_info;
    if (!updateReport.isPending || !updateReport.variables?.layerInfo)
      return layers;
    return updateReport.variables.layerInfo;
  }, [
    specialtyPropertyInfo.report_json.layer_info,
    updateReport.isPending,
    updateReport.variables,
  ]);

  const handleSourceOverrideChange = async (
    layerId: string,
    source: PerilDataSource,
    perilCode: string,
    override: client.SourceOverride | undefined
  ) => {
    const layer = specialtyPropertyInfo.report_json.layer_info.find(
      (L) => L.layer_id === layerId
    );
    if (!layer) return;

    const existing = layer.source_overrides?.[source]?.[perilCode];
    if (override === undefined && existing === undefined) return;
    if (
      override !== undefined &&
      existing !== undefined &&
      existing.multiple === override.multiple &&
      existing.premium === override.premium
    )
      return;

    const newLayers = cloneDeep(specialtyPropertyInfo.report_json.layer_info);
    const newLayer = newLayers.find((L) => L.layer_id === layerId)!;

    newLayer.source_overrides = newLayer.source_overrides ?? {};
    newLayer.source_overrides[source] = newLayer.source_overrides[source] ?? {};

    if (override !== undefined) {
      newLayer.source_overrides[source][perilCode] = override;
    } else {
      delete newLayer.source_overrides[source][perilCode];
    }

    await updateReport.mutateAsync({ layerInfo: newLayers });
  };

  const premiumRows = useMemo((): PremiumPerilRow[] => {
    const rows: PremiumPerilRow[] = [];

    for (const layer of sortedLayers) {
      const layerId = layer.layer_id!;
      if (!enabledLayers.includes(layerId)) continue;
      const layerPremium = premiumByLayer.get(layerId);
      const resolvedLayer = resolvedLayerInfo.find(
        (L) => L.layer_id === layerId
      );
      if (!resolvedLayer) continue;

      let isFirst = true;
      if (!layerPremium) continue;
      for (const perilTypeOptionsBySource of sortedPerilTypes(
        layerPremium.peril_types
      )) {
        const firstOption = Object.values(perilTypeOptionsBySource)[0];
        if (!firstOption) continue;
        const perilType = firstOption.peril_type;

        const premiumsBySource: {
          [key: string]: SourcePremiumData | undefined;
        } = {};
        for (const perilTypeOption of Object.values(perilTypeOptionsBySource)) {
          const source = perilTypeOption.source;
          const parts = perilTypeOption.parts;

          if (parts.length === 1) {
            // Single-code peril type — the peril-type row proxies for the code.
            const part = parts[0]!;
            const resolvedOv =
              resolvedLayer.source_overrides?.[source]?.[part.peril_code];
            premiumsBySource[source] = buildCodeSourceData(part, resolvedOv);
            continue;
          }

          // Multi-code peril type (e.g. aop = [AOP, PWW]) — peril-type row
          // aggregates and is not directly editable; sub-rows carry the edits.
          const missingDataLines: string[] = [];
          const warningLines: string[] = [];
          const multipleTooltipLines: string[] = [];
          const allWarningKeys: string[] = [];
          let anyStale = false;

          for (const part of parts) {
            const ov =
              resolvedLayer.source_overrides?.[source]?.[part.peril_code];
            const backendMul =
              part.override_type === "multiple"
                ? part.override_multiple
                : undefined;
            const backendPrem =
              part.override_type === "premium"
                ? part.override_premium
                : undefined;
            if (
              norm(ov?.multiple) !== norm(backendMul) ||
              norm(ov?.premium) !== norm(backendPrem)
            ) {
              anyStale = true;
            }
            if (part.missing) {
              missingDataLines.push(
                `Missing data for ${perilCodeDisplayName(part.peril_code)}`
              );
            }
            for (const [key, count] of Object.entries(part.warnings)) {
              const label = WARNING_DISPLAY_NAMES[key] ?? key;
              warningLines.push(
                `${label} (${count} location${count !== 1 ? "s" : ""})`
              );
              allWarningKeys.push(key);
            }
            if (part.multiplier != null) {
              multipleTooltipLines.push(
                `${perilCodeDisplayName(part.peril_code)}: ${part.multiplier.toFixed(1)}x (${part.multiplier_reason})`
              );
            }
          }

          const allWarnings = [...warningLines, ...missingDataLines];
          const warningsTooltip =
            allWarnings.length > 0 ? allWarnings.join("\n") : null;
          const multipleTooltip =
            multipleTooltipLines.length === 0
              ? null
              : multipleTooltipLines.join("\n");

          premiumsBySource[source] = {
            aal: perilTypeOption.total_aal ?? 0,
            multiple: perilTypeOption.multiple_effective,
            multipleTooltip,
            layerPremium: perilTypeOption.layer_effective,
            sharePremium: perilTypeOption.share_effective,
            warningsTooltip,
            missingData: missingDataLines.length > 0,
            warningLevel: maxWarningLevel(allWarningKeys),
            overrideType: null,
            originalMultiple: null,
            originalLayerPremium: null,
            originalSharePremium: null,
            stale: anyStale,
            editablePerilCode: null,
            acceptsMultipleOverride: false,
          };
        }

        // Read the resolved source from the layer breakdown.
        const resolvedSource =
          layerPremium.resolved_sources[perilType] ?? "rms";

        const perilSourceData = {
          air: premiumsBySource["air"] ?? EMPTY_SOURCE_DATA,
          rms: premiumsBySource["rms"] ?? EMPTY_SOURCE_DATA,
          spatialkat: premiumsBySource["spatialkat"] ?? EMPTY_SOURCE_DATA,
        };
        const resolved = resolveRowData(perilSourceData, resolvedSource);
        const parts = resolvedSource
          ? (perilTypeOptionsBySource[resolvedSource]?.parts ?? [])
          : [];

        rows.push({
          layer: layer,
          layerId: layerId,
          peril: perilType,
          rowKind: "perilType",
          ...perilSourceData,
          resolvedSource,
          resolved,
          target: null,
          isFirstInGroup: isFirst,
          partSources: parts.map((part) =>
            displayPerilDataSource(part.peril_code, resolvedSource)
          ),
        });
        isFirst = false;

        // Emit peril-code sub-rows (one per peril code in this peril type).
        // Build the code list in a fixed, source-independent order: walk every
        // source's parts and append codes in first-seen order.
        const allCodes: string[] = [];
        const seenCodes = new Set<string>();
        for (const opt of Object.values(perilTypeOptionsBySource)) {
          for (const part of opt.parts) {
            if (!seenCodes.has(part.peril_code)) {
              seenCodes.add(part.peril_code);
              allCodes.push(part.peril_code);
            }
          }
        }
        // Only show sub-rows when there are multiple codes — a single code
        // would just duplicate the peril-type row above.
        if (allCodes.length > 1) {
          for (const code of allCodes) {
            const codeBySource: {
              [key: string]: SourcePremiumData | undefined;
            } = {};
            for (const opt of Object.values(perilTypeOptionsBySource)) {
              const part = opt.parts.find((p) => p.peril_code === code);
              if (!part) continue;
              const ov = resolvedLayer.source_overrides?.[opt.source]?.[code];
              codeBySource[opt.source] = buildCodeSourceData(part, ov);
            }
            const codeSourceData = {
              air: codeBySource["air"] ?? EMPTY_SOURCE_DATA,
              rms: codeBySource["rms"] ?? EMPTY_SOURCE_DATA,
              spatialkat: codeBySource["spatialkat"] ?? EMPTY_SOURCE_DATA,
            };
            rows.push({
              layer: layer,
              layerId: layerId,
              peril: perilType,
              rowKind: "perilCode",
              perilCode: code,
              ...codeSourceData,
              resolvedSource,
              resolved: resolveRowData(codeSourceData, resolvedSource),
              target: null,
              isFirstInGroup: false,
            });
          }
        }
      }

      // Commission adjustment row — only shown when the factor is non-trivial.
      // The factor is already baked into the per-peril and Total values; this
      // row exists purely to make the math visible: AAL stays untouched while
      // a multiplier scales the gross premium.
      const commissionFactor = layerPremium.commission_adjustment_factor;
      const commissionPercent =
        layerPremium.broker_commission_percentage ?? null;
      if (commissionFactor != null && commissionFactor !== 1.0) {
        rows.push({
          layer: layer,
          layerId: layerId,
          peril: "Commission Adj.",
          rowKind: "commissionAdj",
          factor: commissionFactor,
          commissionPercent,
          air: EMPTY_SOURCE_DATA,
          rms: EMPTY_SOURCE_DATA,
          spatialkat: EMPTY_SOURCE_DATA,
          resolved: EMPTY_SOURCE_DATA,
          target: null,
          isFirstInGroup: false,
        });
      }

      // Total row
      const airTotals = layerPremium.totals["air"];
      const rmsTotals = layerPremium.totals["rms"];
      const spatialkatTotals = layerPremium.totals["spatialkat"];
      const resolvedTotals = layerPremium.totals["resolved"];

      // Aggregate AAL from peril-type rows for this layer
      const layerRows = rows.filter(
        (r) => r.layerId === layerId && r.rowKind === "perilType"
      );
      const airTotalAal = layerRows.reduce((s, r) => s + (r.air.aal ?? 0), 0);
      const rmsTotalAal = layerRows.reduce((s, r) => s + (r.rms.aal ?? 0), 0);
      const spatialkatTotalAal = layerRows.reduce(
        (s, r) => s + (r.spatialkat.aal ?? 0),
        0
      );
      const resolvedTotalAal = layerRows.reduce(
        (s, r) => s + (r.resolved.aal ?? 0),
        0
      );
      const airStale = layerRows.some((r) => r.air.stale);
      const rmsStale = layerRows.some((r) => r.rms.stale);
      const spatialkatStale = layerRows.some((r) => r.spatialkat.stale);
      const resolvedStale = layerRows.some((r) => r.resolved.stale);

      const buildTotalSourceData = (
        aal: number,
        totals:
          | {
              layer_total: number;
              share_total: number;
              has_missing_data: boolean;
            }
          | undefined,
        stale: boolean
      ): SourcePremiumData => ({
        aal,
        multiple: aal > 0 && totals ? totals.layer_total / aal : null,
        multipleTooltip: null,
        layerPremium: totals?.layer_total ?? null,
        sharePremium: totals?.share_total ?? null,
        missingData: totals?.has_missing_data ?? false,
        warningLevel: undefined,
        warningsTooltip: null,
        overrideType: null,
        originalMultiple: null,
        originalLayerPremium: null,
        originalSharePremium: null,
        stale,
        editablePerilCode: null,
        acceptsMultipleOverride: false,
      });

      const totalSourceData = {
        air: buildTotalSourceData(airTotalAal, airTotals, airStale),
        rms: buildTotalSourceData(rmsTotalAal, rmsTotals, rmsStale),
        spatialkat: buildTotalSourceData(
          spatialkatTotalAal,
          spatialkatTotals,
          spatialkatStale
        ),
      };

      rows.push({
        layer: layer,
        layerId: layerId,
        peril: "Total",
        rowKind: "total",
        ...totalSourceData,
        resolved: {
          aal: resolvedTotalAal,
          multiple:
            resolvedTotalAal > 0 && resolvedTotals
              ? resolvedTotals.layer_total / resolvedTotalAal
              : null,
          multipleTooltip: null,
          layerPremium: resolvedTotals?.layer_total ?? null,
          sharePremium: resolvedTotals?.share_total ?? null,
          missingData: resolvedTotals?.has_missing_data ?? false,
          warningLevel: undefined,
          warningsTooltip: null,
          overrideType: null,
          originalMultiple: null,
          originalLayerPremium: null,
          originalSharePremium: null,
          stale: resolvedStale,
          editablePerilCode: null,
          acceptsMultipleOverride: false,
        },
        target: layer.target,
        isFirstInGroup: false,
      });
    }
    return rows;
  }, [sortedLayers, enabledLayers, premiumByLayer, resolvedLayerInfo]);

  const tiv = specialtyPropertyInfo.precomputed_metadata?.total_tiv;

  const rateBody = (layerPremium: number) => {
    if (!tiv) return "";
    const rate = (layerPremium / tiv) * 100;
    return (
      <span style={{ fontFamily: "monospace" }}>{formatTwoDecimals(rate)}</span>
    );
  };

  /** Column groups shown in the data table — one per modeling source. */
  const sources = [
    ["air", "AIR"],
    ["rms", "RMS"],
    ["spatialkat", "Kat Risk"],
  ] as const;

  const overrideBg = isLightMode
    ? HIGHLIGHT_USER_OVERRIDE_LIGHT_MODE
    : HIGHLIGHT_USER_OVERRIDE_DARK_MODE;

  /** Return the background color for warning/missing-data highlights. */
  const warningBg = (data: SourcePremiumData): string | undefined => {
    if (data.warningLevel === "critical")
      return isLightMode
        ? HIGHLIGHT_ERROR_LIGHT_MODE
        : HIGHLIGHT_ERROR_DARK_MODE;
    if (data.warningLevel === "info" || data.missingData)
      return isLightMode
        ? HIGHLIGHT_WARNING_LIGHT_MODE
        : HIGHLIGHT_WARNING_DARK_MODE;
    return undefined;
  };

  /** Whether a column source should be visually muted for the given row. */
  const isMuted = (
    columnSource: PerilDataSource,
    rowData: PremiumPerilRow
  ): boolean => {
    // For total / commission-adjustment rows (no resolvedSource), show all unmuted.
    if (rowData.rowKind === "total" || rowData.rowKind === "commissionAdj") {
      return false;
    }
    return columnSource !== rowData.resolvedSource;
  };

  // ── Shared column body renderers ──
  // These are used by both the Results columns and the provider detail columns
  // to avoid duplicating ~200 lines of render logic.

  const aalBody = (data: SourcePremiumData) => {
    const bg = data.multiple !== null ? warningBg(data) : undefined;
    return (
      <span
        className={bg ? styles.highlightPill : undefined}
        style={{ fontFamily: "monospace", backgroundColor: bg }}
      >
        {data.multiple === null
          ? ""
          : data.aal !== null
            ? formatExactMoneyDollars(data.aal, {
                withRounding: true,
                withDollarSign: true,
              })
            : "-"}
      </span>
    );
  };

  const multipleBody = (
    rowData: PremiumPerilRow,
    data: SourcePremiumData,
    getSource: (rowData: PremiumPerilRow) => PerilDataSource
  ) => {
    const isOverridden = data.overrideType === "multiple";
    const isDerived = data.overrideType === "premium";
    if (data.stale && !isOverridden) return "-";
    const multipleText = data.multiple != null ? data.multiple.toFixed(2) : "-";
    const inner = (
      <span
        className={isOverridden ? styles.highlightPill : undefined}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          fontFamily: "monospace",
          fontStyle: isDerived ? "italic" : undefined,
          backgroundColor: isOverridden ? overrideBg : undefined,
        }}
      >
        {multipleText}
        {isOverridden && (
          <i
            className="pi pi-times"
            style={{ fontSize: "0.65rem", cursor: "pointer", opacity: 0.7 }}
            onClick={(e) => {
              e.stopPropagation();
              if (data.editablePerilCode) {
                handleSourceOverrideChange(
                  rowData.layerId,
                  getSource(rowData),
                  data.editablePerilCode,
                  undefined
                );
              }
            }}
          />
        )}
      </span>
    );
    const tooltipText =
      isOverridden && data.originalMultiple != null
        ? `A user edited this value. The previous value was ${data.originalMultiple.toFixed(2)}`
        : isDerived && data.originalMultiple != null
          ? `A user override affected this value. The previous value was ${data.originalMultiple.toFixed(2)}`
          : data.multipleTooltip;
    if (!tooltipText) return inner;
    return (
      <TooltipWrapper
        tooltipText={tooltipText}
        tooltipProps={{ pt: { text: { style: { whiteSpace: "pre" } } } }}
      >
        {inner}
      </TooltipWrapper>
    );
  };

  const layerPremBody = (
    rowData: PremiumPerilRow,
    data: SourcePremiumData,
    getSource: (rowData: PremiumPerilRow) => PerilDataSource
  ) => {
    const isOverridden = data.overrideType === "premium";
    const isDerived = data.overrideType === "multiple";
    if (data.stale && !isOverridden) return "-";
    const bg = isOverridden ? overrideBg : warningBg(data);
    const value = (
      <span
        className={bg ? styles.highlightPill : undefined}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          fontFamily: "monospace",
          fontStyle: isDerived ? "italic" : undefined,
          backgroundColor: bg,
        }}
      >
        {data.layerPremium !== null
          ? formatExactMoneyDollars(data.layerPremium, {
              withRounding: true,
              withDollarSign: true,
            })
          : "-"}
        {isOverridden && (
          <i
            className="pi pi-times"
            style={{ fontSize: "0.65rem", cursor: "pointer", opacity: 0.7 }}
            onClick={(e) => {
              e.stopPropagation();
              if (data.editablePerilCode) {
                handleSourceOverrideChange(
                  rowData.layerId,
                  getSource(rowData),
                  data.editablePerilCode,
                  undefined
                );
              }
            }}
          />
        )}
      </span>
    );
    const wrappedValue =
      isOverridden && data.originalLayerPremium != null ? (
        <TooltipWrapper
          tooltipText={`A user edited this value. The previous value was ${formatExactMoneyDollars(
            data.originalLayerPremium,
            { withRounding: true, withDollarSign: true }
          )}`}
        >
          {value}
        </TooltipWrapper>
      ) : isDerived && data.originalLayerPremium != null ? (
        <TooltipWrapper
          tooltipText={`A user override affected this value. The previous value was ${formatExactMoneyDollars(
            data.originalLayerPremium,
            { withRounding: true, withDollarSign: true }
          )}`}
        >
          {value}
        </TooltipWrapper>
      ) : (
        value
      );
    if (!data.warningsTooltip) return wrappedValue;
    return (
      <span
        style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
      >
        {wrappedValue}
        <TooltipWrapper tooltipText={data.warningsTooltip}>
          <i
            className="pi pi-question-circle"
            style={{
              fontSize: "0.75rem",
              color: "var(--text-color-secondary)",
              cursor: "help",
            }}
          />
        </TooltipWrapper>
      </span>
    );
  };

  const sharePremBody = (data: SourcePremiumData) => {
    if (data.stale) return "-";
    const isDerived = data.overrideType != null;
    const bg = warningBg(data);
    const inner = (
      <span
        className={bg ? styles.highlightPill : undefined}
        style={{
          fontFamily: "monospace",
          fontStyle: isDerived ? "italic" : undefined,
          backgroundColor: bg,
        }}
      >
        {data.sharePremium !== null
          ? formatExactMoneyDollars(data.sharePremium, {
              withRounding: true,
              withDollarSign: true,
            })
          : "-"}
      </span>
    );
    if (isDerived && data.originalSharePremium != null) {
      return (
        <TooltipWrapper
          tooltipText={`A user override affected this value. The previous value was ${formatExactMoneyDollars(
            data.originalSharePremium,
            { withRounding: true, withDollarSign: true }
          )}`}
        >
          {inner}
        </TooltipWrapper>
      );
    }
    return inner;
  };

  /** Whether the Multiple cell is editable for this source-cell. */
  const isMultipleEditable = (data: SourcePremiumData): boolean => {
    return data.editablePerilCode != null && data.acceptsMultipleOverride;
  };

  /** Whether the Layer Premium cell is editable for this source-cell. */
  const isLayerPremEditable = (data: SourcePremiumData): boolean => {
    return data.editablePerilCode != null;
  };

  /** Shared edit handler for the Multiple column. */
  const multipleEditComplete = (
    options: { rowData: PremiumPerilRow; newValue: unknown },
    getData: (r: PremiumPerilRow) => SourcePremiumData,
    getSource: (r: PremiumPerilRow) => PerilDataSource
  ) => {
    const rowData = options.rowData;
    const data = getData(rowData);
    if (!isMultipleEditable(data)) return;
    const newValue = options.newValue as number | null | undefined;
    if (newValue === undefined) return;
    const editorInit = data.multiple;
    if (
      newValue == editorInit ||
      (newValue != null &&
        editorInit != null &&
        Math.abs(newValue - editorInit) < 0.001)
    )
      return;
    handleSourceOverrideChange(
      rowData.layerId,
      getSource(rowData),
      data.editablePerilCode!,
      newValue != null ? { multiple: newValue } : undefined
    );
  };

  /** Shared edit handler for the Layer Premium column. */
  const layerPremEditComplete = (
    options: { rowData: PremiumPerilRow; newValue: unknown },
    getData: (r: PremiumPerilRow) => SourcePremiumData,
    getSource: (r: PremiumPerilRow) => PerilDataSource
  ) => {
    const rowData = options.rowData;
    const data = getData(rowData);
    if (!isLayerPremEditable(data)) return;
    const newValue = options.newValue as number | null | undefined;
    if (newValue === undefined) return;
    if (newValue == data.layerPremium) return;
    handleSourceOverrideChange(
      rowData.layerId,
      getSource(rowData),
      data.editablePerilCode!,
      newValue != null ? { premium: newValue } : undefined
    );
  };

  const headerColumnGroup = (
    <ColumnGroup>
      <Row>
        <Column header="Layer" rowSpan={2} />
        <Column header="Peril" rowSpan={2} />
        <Column header="Results" colSpan={6} />
        {sourcesExpanded &&
          sources.map(([source, name]) => (
            <Column key={source} header={`Modeled - ${name}`} colSpan={5} />
          ))}
      </Row>
      <Row>
        {/* Results sub-headers */}
        <Column key="results-source" header="Source" />
        <Column key="results-aal" header="AAL" />
        <Column key="results-multiple" header="Multiple" />
        <Column key="results-layer-premium" header="Layer Prem" />
        <Column key="results-rate" header="Rate" />
        <Column key="results-share-premium" header="Share Prem" />
        {/* Provider sub-headers (when expanded) */}
        {sourcesExpanded &&
          sources.flatMap(([source]) => [
            <Column key={`${source}-aal`} header="AAL" />,
            <Column key={`${source}-multiple`} header="Multiple" />,
            <Column key={`${source}-layer-premium`} header="Layer Prem" />,
            <Column key={`${source}-rate`} header="Rate" />,
            <Column key={`${source}-share-premium`} header="Share Prem" />,
          ])}
      </Row>
    </ColumnGroup>
  );

  return (
    <Card title={title || "Layers"}>
      <div className={styles.container}>
        <div className={styles.premiumTable}>
          <div className={styles.premiumTableHeader}>
            <MultiSelect
              value={enabledLayers}
              options={layerOptions}
              onChange={(e) => {
                const newInfo = cloneDeep(
                  specialtyPropertyInfo.report_json.layer_info
                );
                for (const layer of newInfo) {
                  layer.is_enabled = (e.value as string[]).some(
                    (selected) => selected === layer.layer_id
                  );
                }
                updateReport.mutate({ layerInfo: newInfo });
              }}
              placeholder="Select layers"
              style={{ width: "250px" }}
            />
            <Button
              icon="pi pi-plus"
              onClick={openAddDialog}
              tooltip="Add layer"
              text
              raised
              tooltipOptions={{ position: "top" }}
            />
            <Button
              icon={
                sourcesExpanded ? "pi pi-chevron-left" : "pi pi-chevron-right"
              }
              onClick={() => setSourcesExpanded((prev) => !prev)}
              tooltip={
                sourcesExpanded
                  ? "Collapse provider detail"
                  : "Expand provider detail"
              }
              text
              raised
              tooltipOptions={{ position: "top" }}
            />
            {(() => {
              const brokerCommission =
                specialtyPropertyInfo.report_json.policy_info
                  ?.broker_commission_percentage;
              const factor =
                premiumData?.layers?.[0]?.commission_adjustment_factor ?? 1.0;
              const commissionLabel =
                brokerCommission != null
                  ? `${brokerCommission}%`
                  : "17.5% (default)";
              return (
                <TooltipWrapper
                  tooltipText={
                    "Premiums are quoted at a default broker commission of 17.5%. " +
                    "When the actual commission differs, gross premiums are scaled by " +
                    "(1 − 0.175) / (1 − commission) to keep the carrier's net premium constant."
                  }
                >
                  <span className={styles.commissionSummary}>
                    Commission:{" "}
                    <span className={styles.commissionSummaryValue}>
                      {commissionLabel}
                    </span>
                    <span>·</span>
                    Adjustment:{" "}
                    <span className={styles.commissionSummaryValue}>
                      {factor.toFixed(4)}x
                    </span>
                  </span>
                </TooltipWrapper>
              );
            })()}
          </div>
          <div className={styles.perilSourcePicker}>
            <span className={styles.perilSourcePickerLabel}>
              Peril Sources:
            </span>
            {(
              specialtyPropertyInfo.report_json.policy_info?.included_perils ??
              []
            )
              .filter((p) => p !== "equipment_breakdown")
              .sort((a, b) => {
                const ai = PERIL_TYPE_ORDER.indexOf(a);
                const bi = PERIL_TYPE_ORDER.indexOf(b);
                return (
                  (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi)
                );
              })
              .map((peril) => {
                const currentSources =
                  specialtyPropertyInfo.report_json.policy_info?.peril_sources;
                const currentSource = currentSources?.[peril] ?? null;
                // Use the backend-resolved source for this peril
                const backendResolved =
                  premiumData?.layers?.[0]?.resolved_sources?.[peril] ?? null;
                const displaySource = currentSource ?? backendResolved;
                // SpatialKat only supports flood
                const sourceOptions =
                  peril === "flood"
                    ? [
                        { label: "AIR", value: "air" },
                        { label: "RMS", value: "rms" },
                        { label: "KAT", value: "spatialkat" },
                      ]
                    : [
                        { label: "AIR", value: "air" },
                        { label: "RMS", value: "rms" },
                      ];
                return (
                  <span key={peril} className={styles.perilSourceItem}>
                    <span className={styles.perilSourceName}>
                      {readableNameFromPeril(peril)}
                    </span>
                    <Dropdown
                      value={displaySource}
                      options={sourceOptions}
                      onChange={(e) => {
                        const policyInfo = cloneDeep(
                          specialtyPropertyInfo.report_json.policy_info
                        );
                        if (!policyInfo) return;
                        policyInfo.peril_sources =
                          policyInfo.peril_sources ?? {};
                        policyInfo.peril_sources[peril] = e.value;
                        // Mark as edited so it survives report reruns
                        const edited = new Set(policyInfo.edited_fields ?? []);
                        edited.add("peril_sources");
                        policyInfo.edited_fields = [...edited];
                        updateReport.mutate({ policyInfo });
                      }}
                      className={styles.perilSourceDropdown}
                      panelClassName={styles.perilSourcePanel}
                    />
                  </span>
                );
              })}
          </div>
          <Dialog
            header={isEditing ? "Edit Layer" : "Add Layer"}
            visible={showLayerDialog}
            onHide={resetLayerDialog}
            style={{ width: "520px" }}
          >
            <div className={styles.addLayerForm}>
              <label className={styles.addLayerField}>
                <span>Attachment Point ($)</span>
                <InputNumber
                  value={newStartingPoint}
                  onChange={(e) => setNewStartingPoint(e.value ?? null)}
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  maxFractionDigits={0}
                  placeholder="0"
                  disabled={isEditing}
                />
              </label>
              <label className={styles.addLayerField}>
                <span>Limit ($)</span>
                <InputNumber
                  value={newRange}
                  onChange={(e) => setNewRange(e.value ?? null)}
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  maxFractionDigits={0}
                  placeholder="0"
                  disabled={isEditing}
                />
              </label>
              <div className={styles.addLayerField}>
                <span>Participation</span>
                <div className="p-inputgroup">
                  <InputNumber
                    value={newParticipation}
                    onChange={(e) => setNewParticipation(e.value ?? null)}
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    maxFractionDigits={0}
                    placeholder="Same as limit"
                    style={{ flex: 3 }}
                  />
                  <InputNumber
                    value={
                      newParticipation != null && newRange
                        ? (newParticipation / newRange) * 100
                        : null
                    }
                    onChange={(e) => {
                      if (e.value != null && newRange) {
                        setNewParticipation(
                          Math.round((e.value / 100) * newRange)
                        );
                      } else {
                        setNewParticipation(null);
                      }
                    }}
                    suffix="%"
                    maxFractionDigits={2}
                    placeholder="100%"
                    style={{ flex: 1 }}
                  />
                </div>
              </div>
              {layerPreview && (
                <div className={styles.layerPreview}>{layerPreview}</div>
              )}
              <div className={styles.competitorPricingSection}>
                <div className={styles.competitorPricingHeader}>
                  <span>Competitor Pricing</span>
                  <Button
                    icon="pi pi-plus"
                    text
                    size="small"
                    tooltip="Add competitor entry"
                    tooltipOptions={{ position: "top" }}
                    onClick={() =>
                      setNewCompetitorPricing((prev) => [
                        ...prev,
                        { carrier_name: "", price: null, notes: null },
                      ])
                    }
                  />
                </div>
                {newCompetitorPricing.length === 0 && (
                  <div className={styles.competitorPricingEmpty}>
                    No entries yet.
                  </div>
                )}
                {newCompetitorPricing.map((entry, idx) => (
                  <div key={idx} className={styles.competitorPricingEntry}>
                    <div className={styles.competitorPricingTopRow}>
                      <InputText
                        value={entry.carrier_name}
                        onChange={(e) =>
                          setNewCompetitorPricing((prev) =>
                            prev.map((c, i) =>
                              i === idx
                                ? { ...c, carrier_name: e.target.value }
                                : c
                            )
                          )
                        }
                        placeholder="Carrier *"
                        className={
                          entry.carrier_name.trim().length === 0
                            ? "p-invalid"
                            : ""
                        }
                      />
                      <InputNumber
                        value={entry.price ?? null}
                        onChange={(e) =>
                          setNewCompetitorPricing((prev) =>
                            prev.map((c, i) =>
                              i === idx ? { ...c, price: e.value ?? null } : c
                            )
                          )
                        }
                        mode="currency"
                        currency="USD"
                        locale="en-US"
                        maxFractionDigits={0}
                        placeholder="Price"
                      />
                      <Button
                        icon="pi pi-trash"
                        text
                        severity="danger"
                        size="small"
                        onClick={() =>
                          setNewCompetitorPricing((prev) =>
                            prev.filter((_, i) => i !== idx)
                          )
                        }
                      />
                    </div>
                    <InputTextarea
                      value={entry.notes ?? ""}
                      onChange={(e) =>
                        setNewCompetitorPricing((prev) =>
                          prev.map((c, i) =>
                            i === idx
                              ? { ...c, notes: e.target.value || null }
                              : c
                          )
                        )
                      }
                      placeholder="Notes"
                      rows={2}
                      autoResize
                      className={styles.competitorPricingNotes}
                    />
                  </div>
                ))}
              </div>
              <Button
                label={isEditing ? "Save" : "Add"}
                icon={isEditing ? "pi pi-check" : "pi pi-plus"}
                onClick={handleSaveLayer}
                disabled={
                  newStartingPoint == null ||
                  newRange == null ||
                  newRange <= 0 ||
                  !competitorPricingValid ||
                  updateReport.isPending
                }
                loading={updateReport.isPending}
                style={{ marginTop: "8px" }}
              />
            </div>
          </Dialog>
          {firstPremiumLoading ? (
            <div className={styles.loadingContainer}>
              <ProgressSpinner />
            </div>
          ) : premiumError ? (
            <div className={styles.errorContainer}>
              Something went wrong calculating premiums. Let us know which
              report you were working on in #engineering.
            </div>
          ) : (
            <div
              className={`pantheon-small-table ${styles.scrollTopWrapper} ${updateReport.isPending || premiumLoading ? styles.tableStale : ""}`}
            >
              <DataTable<PremiumPerilRow[]>
                value={premiumRows}
                size="small"
                headerColumnGroup={headerColumnGroup}
                rowGroupMode="rowspan"
                groupRowsBy="layerId"
                scrollHeight="70vh"
                tableStyle={{ width: "auto" }}
                rowClassName={(rowData: PremiumPerilRow) => {
                  const classes: string[] = [];
                  if (rowData.rowKind === "total") {
                    classes.push(styles.layerTotalRow);
                  }
                  if (rowData.rowKind === "perilCode") {
                    classes.push(styles.perilCodeRow);
                  }
                  if (rowData.rowKind === "commissionAdj") {
                    classes.push(styles.commissionAdjRow);
                  }
                  if (rowData.isFirstInGroup) {
                    classes.push(styles.layerGroupStart);
                  }
                  return classes.join(" ");
                }}
                editMode="cell"
              >
                <Column
                  field="layerId"
                  style={{ width: "200px" }}
                  bodyClassName={styles.layerCell}
                  body={(rowData: PremiumPerilRow) => {
                    const layer =
                      specialtyPropertyInfo.report_json.layer_info.find(
                        (L) => L.layer_id === rowData.layerId
                      );
                    return (
                      <div>
                        <span className={styles.layerNameRow}>
                          <span>{readableLayer(rowData.layer)}</span>
                          {rowData.isFirstInGroup && layer && (
                            <i
                              className="pi pi-pencil"
                              style={{
                                fontSize: "0.75rem",
                                cursor: "pointer",
                                opacity: 0.6,
                              }}
                              onClick={() => openEditDialog(layer)}
                            />
                          )}
                        </span>
                        {rowData.isFirstInGroup && (
                          <>
                            <div className={styles.perilSentence}>
                              {perilSentenceFromEnabledPerils(
                                getEffectiveIncludedPerils(
                                  specialtyPropertyInfo.report_json.policy_info
                                    ?.included_perils ?? [],
                                  premiumData,
                                  rowData.layerId
                                ),
                                specialtyPropertyInfo.report_json.policy_info
                                  ?.peril_sentence_override
                              )}
                            </div>
                            <div className={styles.layerMeta}>
                              {(() => {
                                const { participation, range } = rowData.layer;
                                const pct =
                                  participation != null
                                    ? (participation / range) * 100
                                    : 100;
                                const pctStr =
                                  pct % 1 === 0
                                    ? pct.toFixed(0)
                                    : pct.toFixed(1);
                                return (
                                  <span>
                                    Part:{" "}
                                    <span style={{ fontFamily: "monospace" }}>
                                      {pctStr}%
                                    </span>
                                  </span>
                                );
                              })()}
                              {rowData.layer.target != null && (
                                <span>
                                  Target:{" "}
                                  <span style={{ fontFamily: "monospace" }}>
                                    {formatExactMoneyDollars(
                                      rowData.layer.target,
                                      {
                                        withRounding: true,
                                        withDollarSign: true,
                                      }
                                    )}
                                  </span>
                                  {tiv ? (
                                    <span
                                      style={{
                                        fontFamily: "monospace",
                                        marginLeft: "4px",
                                      }}
                                    >
                                      (
                                      {(
                                        (rowData.layer.target / tiv) *
                                        100
                                      ).toFixed(2)}
                                      )
                                    </span>
                                  ) : null}
                                </span>
                              )}
                            </div>
                            {(rowData.layer.competitor_pricing ?? []).length >
                              0 && (
                              <div className={styles.competitorPricingList}>
                                <div
                                  className={styles.competitorPricingListLabel}
                                >
                                  Competitor Pricing
                                </div>
                                {(rowData.layer.competitor_pricing ?? []).map(
                                  (entry, idx) => (
                                    <div
                                      key={idx}
                                      className={styles.competitorPricingPill}
                                    >
                                      <span
                                        className={
                                          styles.competitorPricingCarrier
                                        }
                                      >
                                        {entry.carrier_name}
                                      </span>
                                      {entry.price != null && (
                                        <span
                                          className={
                                            styles.competitorPricingPrice
                                          }
                                        >
                                          {formatExactMoneyDollars(
                                            entry.price,
                                            {
                                              withRounding: true,
                                              withDollarSign: true,
                                            }
                                          )}
                                        </span>
                                      )}
                                      {entry.notes && (
                                        <TooltipWrapper
                                          tooltipText={entry.notes}
                                        >
                                          <i
                                            className="pi pi-info-circle"
                                            style={{
                                              fontSize: "0.7rem",
                                              opacity: 0.7,
                                              cursor: "help",
                                            }}
                                          />
                                        </TooltipWrapper>
                                      )}
                                    </div>
                                  )
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  }}
                />
                <Column
                  field="peril"
                  style={{ width: "200px" }}
                  body={(rowData: PremiumPerilRow) => {
                    if (rowData.rowKind === "total") {
                      return <span className={styles.totalPeril}>Total</span>;
                    }
                    if (rowData.rowKind === "commissionAdj") {
                      const tooltipText =
                        rowData.commissionPercent != null
                          ? `Gross premium is scaled by (1 − 17.5%) / (1 − ${rowData.commissionPercent}%) so the carrier's net premium is unchanged. AAL is not modified.`
                          : "Gross premium adjustment applied because the broker commission differs from the 17.5% default. AAL is not modified.";
                      return (
                        <TooltipWrapper tooltipText={tooltipText}>
                          <span className={styles.commissionAdjLabel}>
                            Commission Adj.
                          </span>
                        </TooltipWrapper>
                      );
                    }
                    if (rowData.rowKind === "perilCode") {
                      return (
                        <span className={styles.perilCodeLabel}>
                          {perilCodeDisplayName(rowData.perilCode)}
                        </span>
                      );
                    }
                    return readableNameFromPeril(rowData.peril);
                  }}
                />
                {/* ── Results columns (always visible) ── */}
                <Column
                  key="results-source"
                  style={{ width: "55px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  body={(rowData: PremiumPerilRow) => {
                    if (rowData.rowKind === "total") return "";
                    if (rowData.rowKind === "commissionAdj") return "";
                    if (!rowData.resolvedSource) return "";

                    const resolved =
                      rowData.rowKind === "perilCode"
                        ? displayPerilDataSource(
                            rowData.perilCode,
                            rowData.resolvedSource
                          )
                        : displayPerilDataSource2(rowData);

                    const badgeCls =
                      styles[SOURCE_BADGE_CLASSES[resolved]] ?? "";
                    return (
                      <span className={`${styles.sourceLabel} ${badgeCls}`}>
                        {SOURCE_LABELS[resolved]}
                      </span>
                    );
                  }}
                />
                <Column
                  key="results-aal"
                  style={{ width: "100px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  body={(rowData: PremiumPerilRow) => aalBody(rowData.resolved)}
                />
                <Column
                  key="results-multiple"
                  style={{ width: "90px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  onCellEditComplete={(options) =>
                    multipleEditComplete(
                      options,
                      (r) => r.resolved,
                      getRowSource
                    )
                  }
                  onBeforeCellEditShow={(options) =>
                    isMultipleEditable(options.rowData.resolved)
                  }
                  editor={(options) => (
                    <InputNumber
                      value={options.rowData.resolved.multiple}
                      onValueChange={(e) => options.editorCallback?.(e.value)}
                      minFractionDigits={1}
                      className={styles.overrideInput}
                      placeholder="-"
                      disabled={updateReport.isPending}
                    />
                  )}
                  body={(rowData: PremiumPerilRow) => {
                    if (rowData.rowKind === "commissionAdj") {
                      return (
                        <span className={styles.commissionAdjFactor}>
                          {rowData.factor.toFixed(4)}×
                        </span>
                      );
                    }
                    return multipleBody(
                      rowData,
                      rowData.resolved,
                      getRowSource
                    );
                  }}
                />
                <Column
                  key="results-layer-premium"
                  style={{ width: "110px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  onCellEditComplete={(options) =>
                    layerPremEditComplete(
                      options,
                      (r) => r.resolved,
                      getRowSource
                    )
                  }
                  onBeforeCellEditShow={(options) =>
                    isLayerPremEditable(options.rowData.resolved)
                  }
                  editor={(options) => (
                    <InputNumber
                      value={options.rowData.resolved.layerPremium}
                      onValueChange={(e) => options.editorCallback?.(e.value)}
                      mode="currency"
                      currency="USD"
                      locale="en-US"
                      maxFractionDigits={0}
                      className={styles.overrideInput}
                      placeholder="-"
                      disabled={updateReport.isPending}
                    />
                  )}
                  body={(rowData: PremiumPerilRow) =>
                    layerPremBody(rowData, rowData.resolved, getRowSource)
                  }
                />
                <Column
                  key="results-rate"
                  style={{ width: "70px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  body={(rowData: PremiumPerilRow) => {
                    const data = rowData.resolved;
                    return data.stale || data.layerPremium === null
                      ? "-"
                      : rateBody(data.layerPremium);
                  }}
                />
                <Column
                  key="results-share-premium"
                  style={{ width: "110px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  body={(rowData: PremiumPerilRow) =>
                    sharePremBody(rowData.resolved)
                  }
                />
                {/* ── Provider detail columns (expanded only) ── */}
                {sourcesExpanded &&
                  sources.flatMap(([source]) => {
                    const mutedCls = (rowData: PremiumPerilRow) =>
                      isMuted(source, rowData) ? styles.mutedColumn : "";
                    const getData = (r: PremiumPerilRow) => r[source];
                    return [
                      <Column
                        key={`${source}-aal`}
                        style={{ width: "100px" }}
                        bodyClassName={mutedCls}
                        body={(rowData: PremiumPerilRow) =>
                          aalBody(getData(rowData))
                        }
                      />,
                      <Column
                        key={`${source}-multiple`}
                        style={{ width: "90px" }}
                        bodyClassName={mutedCls}
                        onCellEditComplete={(options) =>
                          multipleEditComplete(options, getData, () => source)
                        }
                        onBeforeCellEditShow={(options) =>
                          isMultipleEditable(getData(options.rowData))
                        }
                        editor={(options) => (
                          <InputNumber
                            value={getData(options.rowData).multiple}
                            onValueChange={(e) =>
                              options.editorCallback?.(e.value)
                            }
                            minFractionDigits={1}
                            className={styles.overrideInput}
                            placeholder="-"
                            disabled={updateReport.isPending}
                          />
                        )}
                        body={(rowData: PremiumPerilRow) =>
                          multipleBody(rowData, getData(rowData), () => source)
                        }
                      />,
                      <Column
                        key={`${source}-layer-premium`}
                        style={{ width: "110px" }}
                        bodyClassName={mutedCls}
                        onCellEditComplete={(options) =>
                          layerPremEditComplete(options, getData, () => source)
                        }
                        onBeforeCellEditShow={(options) =>
                          isLayerPremEditable(getData(options.rowData))
                        }
                        editor={(options) => (
                          <InputNumber
                            value={getData(options.rowData).layerPremium}
                            onValueChange={(e) =>
                              options.editorCallback?.(e.value)
                            }
                            mode="currency"
                            currency="USD"
                            locale="en-US"
                            maxFractionDigits={0}
                            className={styles.overrideInput}
                            placeholder="-"
                            disabled={updateReport.isPending}
                          />
                        )}
                        body={(rowData: PremiumPerilRow) =>
                          layerPremBody(rowData, getData(rowData), () => source)
                        }
                      />,
                      <Column
                        key={`${source}-rate`}
                        style={{ width: "70px" }}
                        bodyClassName={mutedCls}
                        body={(rowData: PremiumPerilRow) => {
                          const data = getData(rowData);
                          return data.stale || data.layerPremium === null
                            ? "-"
                            : rateBody(data.layerPremium);
                        }}
                      />,
                      <Column
                        key={`${source}-share-premium`}
                        style={{ width: "110px" }}
                        bodyClassName={mutedCls}
                        body={(rowData: PremiumPerilRow) =>
                          sharePremBody(getData(rowData))
                        }
                      />,
                    ];
                  })}
              </DataTable>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
